import { useState, useEffect } from "react";
import { Link } from "react-router";
import { useCustomerAuth } from "./CustomerAuthContext";
import { useTheme } from "./ThemeInjector";
import { useLanguage } from "./LanguageContext";
import { LogOut, Package, User, Mail, Eye, EyeOff, ArrowLeft } from "lucide-react";

type Tab = "orders" | "profile";

export function AccountPage() {
  const { customer, isAuthenticated, isAvailable, isLoading, signIn, signUp, logOut, orders, loadOrders } = useCustomerAuth();
  const theme = useTheme();
  const { t } = useLanguage();
  const [tab, setTab] = useState<Tab>("orders");

  // Auth form state
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [signUpSuccess, setSignUpSuccess] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      loadOrders();
    }
  }, [isAuthenticated]);

  // If Supabase is not connected, show a placeholder
  if (!isAvailable) {
    return (
      <div className="pt-32 pb-24 px-6 max-w-2xl mx-auto text-center">
        <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: `${theme.accent}1a` }}>
          <User size={28} style={{ color: theme.accent }} />
        </div>
        <h1
          className="tracking-[0.1em] mb-4"
          style={{ fontFamily: theme.fontHeading, fontSize: "clamp(28px, 4vw, 42px)", fontWeight: 400, color: theme.fg }}
        >
          {t("account.title")}
        </h1>
        <p
          className="leading-[1.8] mb-8 max-w-md mx-auto"
          style={{ fontFamily: theme.fontBody, fontSize: "15px", fontWeight: 300, color: theme.muted }}
        >
          {t("account.coming_soon")}
        </p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 px-8 py-3 tracking-[0.2em] uppercase transition-all duration-300"
          style={{ fontFamily: theme.fontBody, fontSize: "12px", backgroundColor: theme.btnBg, color: theme.btnText, borderRadius: theme.btnRadius }}
        >
          <ArrowLeft size={14} />
          {t("account.back_home")}
        </Link>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="pt-32 pb-24 px-6 max-w-lg mx-auto text-center">
        <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin mx-auto" style={{ borderColor: `${theme.accent} transparent ${theme.accent} ${theme.accent}` }} />
      </div>
    );
  }

  // ─── Not authenticated: show sign-in / sign-up form ───
  if (!isAuthenticated) {
    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      setError("");
      setSubmitting(true);

      if (mode === "signin") {
        const result = await signIn(email, password);
        if (!result.success) setError(result.error || "Sign in failed");
      } else {
        if (password.length < 8) {
          setError("Password must be at least 8 characters");
          setSubmitting(false);
          return;
        }
        const result = await signUp(email, password, displayName || undefined);
        if (!result.success) {
          setError(result.error || "Sign up failed");
        } else {
          setSignUpSuccess(true);
        }
      }
      setSubmitting(false);
    };

    if (signUpSuccess) {
      return (
        <div className="pt-32 pb-24 px-6 max-w-md mx-auto text-center">
          <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: `${theme.primary}1a` }}>
            <Mail size={24} style={{ color: theme.primary }} />
          </div>
          <h2 className="tracking-[0.1em] mb-4" style={{ fontFamily: theme.fontHeading, fontSize: "28px", fontWeight: 400, color: theme.fg }}>
            Check your email
          </h2>
          <p className="leading-[1.8]" style={{ fontFamily: theme.fontBody, fontSize: "15px", fontWeight: 300, color: theme.muted }}>
            We've sent a confirmation link to <strong style={{ color: theme.fg }}>{email}</strong>. Click the link to activate your account.
          </p>
          <button
            onClick={() => { setSignUpSuccess(false); setMode("signin"); }}
            className="mt-8 tracking-[0.15em] uppercase transition-colors"
            style={{ fontFamily: theme.fontBody, fontSize: "12px", color: theme.accent }}
          >
            Back to sign in
          </button>
        </div>
      );
    }

    return (
      <div className="pt-32 pb-24 px-6 max-w-sm mx-auto">
        <h1
          className="text-center tracking-[0.1em] mb-2"
          style={{ fontFamily: theme.fontHeading, fontSize: "32px", fontWeight: 400, color: theme.fg }}
        >
          {mode === "signin" ? t("account.sign_in") : t("account.sign_up")}
        </h1>
        <p
          className="text-center mb-10"
          style={{ fontFamily: theme.fontBody, fontSize: "14px", fontWeight: 300, color: theme.muted }}
        >
          {mode === "signin" ? t("account.sign_in_desc") : t("account.sign_up_desc")}
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === "signup" && (
            <div>
              <label className="block tracking-[0.12em] uppercase mb-2" style={{ fontSize: "10px", color: theme.muted }}>
                {t("account.name")}
              </label>
              <input
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                maxLength={100}
                placeholder="Your name"
                className="w-full px-4 py-3 border rounded-lg focus:outline-none transition-colors"
                style={{ fontSize: "14px", fontWeight: 300, fontFamily: theme.fontBody, backgroundColor: theme.bg, borderColor: theme.border, color: theme.fg }}
              />
            </div>
          )}

          <div>
            <label className="block tracking-[0.12em] uppercase mb-2" style={{ fontSize: "10px", color: theme.muted }}>
              {t("account.email")}
            </label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              maxLength={200}
              placeholder="your@email.com"
              className="w-full px-4 py-3 border rounded-lg focus:outline-none transition-colors"
              style={{ fontSize: "14px", fontWeight: 300, fontFamily: theme.fontBody, backgroundColor: theme.bg, borderColor: theme.border, color: theme.fg }}
            />
          </div>

          <div>
            <label className="block tracking-[0.12em] uppercase mb-2" style={{ fontSize: "10px", color: theme.muted }}>
              {t("account.password")}
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                maxLength={128}
                placeholder={mode === "signup" ? "Min. 8 characters" : "Enter password"}
                className="w-full px-4 py-3 border rounded-lg focus:outline-none transition-colors pr-12"
                style={{ fontSize: "14px", fontWeight: 300, fontFamily: theme.fontBody, backgroundColor: theme.bg, borderColor: theme.border, color: theme.fg }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors"
                style={{ color: theme.muted }}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {error && (
            <p style={{ fontSize: "13px", color: "#8B5C58" }}>{error}</p>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full py-3.5 tracking-[0.2em] uppercase transition-all duration-300 disabled:opacity-50"
            style={{ fontSize: "12px", fontWeight: 400, fontFamily: theme.fontBody, backgroundColor: theme.btnBg, color: theme.btnText, borderRadius: theme.btnRadius }}
          >
            {submitting ? "..." : mode === "signin" ? t("account.sign_in") : t("account.sign_up")}
          </button>
        </form>

        <p className="text-center mt-8" style={{ fontSize: "13px", fontWeight: 300, color: theme.muted }}>
          {mode === "signin" ? t("account.no_account") : t("account.have_account")}{" "}
          <button
            onClick={() => { setMode(mode === "signin" ? "signup" : "signin"); setError(""); }}
            className="underline transition-colors"
            style={{ color: theme.accent }}
          >
            {mode === "signin" ? t("account.sign_up") : t("account.sign_in")}
          </button>
        </p>
      </div>
    );
  }

  // ─── Authenticated: show account dashboard ───
  return (
    <div className="pt-28 pb-24 px-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-10">
        <div>
          <h1 className="tracking-[0.1em] mb-1" style={{ fontFamily: theme.fontHeading, fontSize: "32px", fontWeight: 400, color: theme.fg }}>
            {t("account.title")}
          </h1>
          <p style={{ fontFamily: theme.fontBody, fontSize: "14px", fontWeight: 300, color: theme.muted }}>
            {customer?.display_name || customer?.email}
          </p>
        </div>
        <button
          onClick={logOut}
          className="flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors"
          style={{ fontSize: "12px", fontFamily: theme.fontBody, borderColor: theme.border, color: theme.muted, borderRadius: theme.btnRadius }}
        >
          <LogOut size={14} />
          {t("account.sign_out")}
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-6 mb-8 pb-3" style={{ borderBottom: `1px solid ${theme.border}` }}>
        {([
          { key: "orders" as Tab, label: t("account.orders"), icon: Package },
          { key: "profile" as Tab, label: t("account.profile"), icon: User },
        ]).map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className="flex items-center gap-2 pb-1 tracking-[0.1em] uppercase transition-colors"
            style={{
              fontSize: "11px",
              fontFamily: theme.fontBody,
              color: tab === key ? theme.primary : theme.muted,
              borderBottom: tab === key ? `2px solid ${theme.primary}` : "2px solid transparent",
            }}
          >
            <Icon size={14} />
            {label}
          </button>
        ))}
      </div>

      {/* Orders tab */}
      {tab === "orders" && (
        <div>
          {orders.length === 0 ? (
            <div className="text-center py-16">
              <Package size={32} className="mx-auto mb-4" style={{ color: theme.border }} />
              <p style={{ fontFamily: theme.fontBody, fontSize: "15px", fontWeight: 300, color: theme.muted }}>
                {t("account.no_orders")}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {orders.map((order) => (
                <div key={order.id} className="rounded-xl p-5 shadow-[0_2px_16px_rgba(40,32,20,0.04)]" style={{ backgroundColor: theme.card }}>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="tracking-[0.1em]" style={{ fontFamily: theme.fontBody, fontSize: "14px", color: theme.fg }}>
                        {order.order_number}
                      </p>
                      <p style={{ fontSize: "11px", color: theme.muted }}>
                        {new Date(order.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <span
                      className="px-3 py-1 rounded-full tracking-[0.1em] uppercase"
                      style={{
                        fontSize: "10px",
                        backgroundColor: order.status === "delivered" ? `${theme.primary}15` : `${theme.accent}15`,
                        color: order.status === "delivered" ? theme.primary : theme.accent,
                      }}
                    >
                      {order.status}
                    </span>
                  </div>
                  <div className="space-y-1">
                    {order.order_items?.map((item, idx) => (
                      <div key={idx} className="flex justify-between" style={{ fontSize: "13px", fontWeight: 300, color: theme.muted }}>
                        <span>{item.quantity} × {item.product_name}</span>
                        <span>€{(item.unit_price * item.quantity).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 pt-3 flex justify-between" style={{ borderTop: `1px solid ${theme.border}` }}>
                    <span className="tracking-[0.1em] uppercase" style={{ fontSize: "11px", color: theme.muted }}>Total</span>
                    <span style={{ fontFamily: theme.fontHeading, fontSize: "18px", color: theme.fg }}>
                      €{(order.subtotal - (order.discount_amount || 0)).toFixed(2)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Profile tab */}
      {tab === "profile" && customer && (
        <div className="rounded-xl p-6 shadow-[0_2px_16px_rgba(40,32,20,0.04)]" style={{ backgroundColor: theme.card }}>
          <div className="space-y-4">
            <div>
              <label className="block tracking-[0.12em] uppercase mb-1" style={{ fontSize: "10px", color: theme.muted }}>
                {t("account.email")}
              </label>
              <p style={{ fontSize: "14px", color: theme.fg }}>{customer.email}</p>
            </div>
            {customer.display_name && (
              <div>
                <label className="block tracking-[0.12em] uppercase mb-1" style={{ fontSize: "10px", color: theme.muted }}>
                  {t("account.name")}
                </label>
                <p style={{ fontSize: "14px", color: theme.fg }}>{customer.display_name}</p>
              </div>
            )}
            <div>
              <label className="block tracking-[0.12em] uppercase mb-1" style={{ fontSize: "10px", color: theme.muted }}>
                {t("account.member_since")}
              </label>
              <p style={{ fontSize: "14px", color: theme.fg }}>
                {new Date(customer.created_at).toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
